import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ContextMenuOption, ContextMenuState } from '../../models/misc/context-menu';
import CustomContextMenu from '../misc/CustomContextMenu';

const NavigationButton: React.FC<{
  isNavOpen: boolean;
  title: string | React.ReactElement;
  icon?: React.ReactNode;
  disabled?: { disabled: boolean; tooltip: string };
  isCurrentPage?: boolean;
  lowerCase?: boolean;
  hideTitle?: boolean;
  centerAlign?: boolean;
  contextMenuOptions?: ContextMenuOption[];
  invertColors?: boolean;
  isTopBar?: boolean;
  disableActiveBackground?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({
  isNavOpen,
  title,
  icon,
  isCurrentPage = false,
  disabled,
  lowerCase = false,
  hideTitle = false,
  centerAlign = false,
  contextMenuOptions,
  invertColors,
  isTopBar = false,
  disableActiveBackground = false,
  onClick,
}) => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  // An open nav shows the title inline, so it only needs the tooltip once the title is cut off.
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const measureTitleHandler = (event: React.MouseEvent<HTMLElement>) => {
    const titleElement = event.currentTarget.querySelector('[data-nav-title]');
    setIsTitleTruncated(
      titleElement != null && titleElement.scrollWidth > titleElement.clientWidth,
    );
  };

  const openContextMenuHandler = (event: React.MouseEvent) => {
    event.preventDefault();

    setContextMenu(
      contextMenu === null ? { id: '', mouseX: event.clientX, mouseY: event.clientY } : null,
    );
  };

  const closeContextMenuHandler = () => {
    setContextMenu(null);
  };

  return (
    <React.Fragment>
      <Tooltip
        title={
          disabled?.tooltip != null && disabled.disabled === true ? (
            <Typography>{disabled.tooltip}</Typography>
          ) : isNavOpen && !isTitleTruncated ? undefined : (
            title
          )
        }
        placement={centerAlign ? 'top' : 'right'}
        role="tooltip"
      >
        <span
          style={!isTopBar ? { width: '100%', overflow: 'hidden' } : {}}
          onMouseEnter={measureTitleHandler}
        >
          <Button
            onClick={onClick}
            disabled={disabled?.disabled === true}
            sx={(theme) => ({
              flexDirection: centerAlign ? 'column' : 'row',
              alignItems: centerAlign ? 'center' : undefined,
              justifyContent: centerAlign ? 'center' : 'left',
              width: '100%',
              minWidth: '24px',
              overflow: 'hidden',
              height: theme.spacing(6),
              px: 2,
              backgroundColor: invertColors
                ? theme.palette.primary.main
                : isCurrentPage && !disableActiveBackground
                  ? theme.palette.grey[300]
                  : undefined,
              color: invertColors
                ? 'white'
                : isCurrentPage
                  ? theme.palette.primary.main
                  : theme.palette.action.active,
              textTransform: !lowerCase ? 'capitalize' : undefined,
              // The title gives way to the ellipsis; the icon keeps its size.
              '& > :not([data-nav-title])': { flexShrink: 0 },
              '&:hover': {
                backgroundColor: invertColors ? theme.palette.primary.dark : undefined,
              },
            })}
            onContextMenu={contextMenuOptions != null ? openContextMenuHandler : undefined}
          >
            {icon}
            {!hideTitle &&
              (typeof title === 'string' ? (
                <Typography
                  data-nav-title
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flexShrink: centerAlign ? 0 : undefined,
                    maxWidth: '100%',
                    fontSize: centerAlign ? 'small' : undefined,
                    ml: centerAlign || icon == null ? undefined : 2,
                  }}
                >
                  {title}
                </Typography>
              ) : (
                title
              ))}
          </Button>
        </span>
      </Tooltip>
      {contextMenuOptions != null && (
        <CustomContextMenu
          contextMenuOptions={contextMenuOptions}
          contextMenuState={contextMenu}
          onClose={closeContextMenuHandler}
        />
      )}
    </React.Fragment>
  );
};

export default NavigationButton;
